import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { FOSSA_ORG_ID } from '../../../shared/organizations/utils';
import { useOnConfirm } from '../../contexts/DialogContext';
import { window } from '../../globals';
import { useAsyncStatus, useDebouncedCallback, useToggle } from '../../hooks';
import { useDeactivateUser } from '../../hooks/api/users/useDeactivateUser';
import { useReactivateUser } from '../../hooks/api/users/useReactivateUser';
import { useIsOnPrem } from '../../hooks/useIsOnPrem';
import * as api from '../../redux/api';
import Accordion from '../generic/Accordion';
import Button, { BUTTON_TYPE, ButtonGroup } from '../generic/Button';
import Card from '../generic/Card/Card';
import ConfirmModal from '../generic/ConfirmModal';
import TextInput from '../generic/Forms/TextInput';
import SelectableReactTable from '../generic/SelectableReactTable';
import Text from '../generic/Text';
import type { FailedFetchResponse } from '../../helpers/errors';

import ModalAreYouSure from './ModalAreYouSure';
import OrganizationSearch from './OrganizationCard/OrganizationSearch';
import { SearchPrompt } from './SearchPrompt';
import { useInternalToolsParams } from './useInternalToolsParams';

type UserCardUser = {
  id: number;
  email: string;
  username: string;
  enabled: boolean;
  super: boolean;
  tokenCount: number;
  organization: {
    fetcherConfig: unknown;
    title: string;
    id: number;
  }
};

type Props = {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

const UserCard: VoidFunctionComponent<Props> = ({ isOpen, onToggle }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const [users, setUsers] = useState<UserCardUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserCardUser | null>(null);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();
  const [isDeleteModalShowing, onToggleDeleteModal] = useToggle();
  const [isChangeUserOrgModalShowing, onToggleChangeUserOrg] = useToggle();
  const [isDeactivateModalShowing, onToggleDeactivateModal] = useToggle();
  const [isReactivateModalShowing, onToggleReactivateModal] = useToggle();

  const onConfirm = useOnConfirm();
  const isOnPrem = useIsOnPrem();

  const { selectedOrgId, userSearch, setUserSearch } = useInternalToolsParams();

  // Mirror the search term in local state so fast typing never drops characters while the URL
  // updates. Seeded from the URL on mount to support deep links.
  const [emailInput, setEmailInput] = useState(userSearch);

  // Keep the mirror in sync when the URL changes externally (browser back/forward, direct edits).
  useEffect(() => {
    setEmailInput(userSearch);
  }, [userSearch]);

  const fetchUsersByEmail = useDebouncedCallback(async (email: string) => {
    onPending();

    try {
      const { data } = await api.get<UserCardUser[]>('/api/internal-tools/users', false, { email });
      onSuccess();

      setUsers(data ?? []);
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, 500);

  const fetchUsersByEmailRef = useRef(fetchUsersByEmail);
  fetchUsersByEmailRef.current = fetchUsersByEmail;

  // Fetch new data when the search term changes. Skip the request entirely while the search is
  // empty so the page does not fire an empty-email query on load, matching the org search.
  useEffect(() => {
    if (userSearch.trim() === '') {
      setUsers([]);
      setSelectedUser(null);
      return;
    }
    fetchUsersByEmailRef.current(userSearch);
  }, [userSearch]);

  const refreshUsers = useCallback(() => {
    fetchUsersByEmailRef.current(userSearch);
  }, [userSearch]);

  const deleteSelectedUser = useCallback(async () => {
    if (selectedUser === null) {
      return;
    }

    await api.remove(`/api/internal-tools/user/${selectedUser.id}`);
    refreshUsers();
  }, [selectedUser, refreshUsers]);

  const updatedSelectedUser = useCallback(async () => {
    if (selectedUser === null || selectedOrgId === null) {
      return;
    }

    await api.put(`/api/internal-tools/user/${selectedUser.id}`, { organizationId: selectedOrgId });
    refreshUsers();
  }, [selectedUser, selectedOrgId, refreshUsers]);

  const deactivateMutation = useDeactivateUser(() => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, enabled: false });
    }
    refreshUsers();
  });

  const reactivateMutation = useReactivateUser(() => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, enabled: true });
    }
    refreshUsers();
  });

  const deactivateSelectedUser = useCallback(() => {
    if (!selectedUser) {
      return;
    }
    deactivateMutation.mutate(selectedUser.id);
  }, [selectedUser, deactivateMutation]);

  const reactivateSelectedUser = useCallback(() => {
    if (!selectedUser) {
      return;
    }
    reactivateMutation.mutate(selectedUser.id);
  }, [selectedUser, reactivateMutation]);

  const confirmMoveSelectedUserToFossa = useCallback(() => {
    if (!selectedUser) {
      return;
    }
    onConfirm({
      title: `Move ${selectedUser.email} to FOSSA`,
      body: `Are you sure you want to move ${selectedUser.email} to the FOSSA organization (ID ${FOSSA_ORG_ID})?`,
      confirmText: 'Move to FOSSA',
      onConfirm: async () => {
        try {
          await api.put(`/api/internal-tools/user/${selectedUser.id}`, { organizationId: FOSSA_ORG_ID });
          refreshUsers();
        } catch (err) {
          onError(err as FailedFetchResponse);
        }
      },
    });
  }, [selectedUser, onConfirm, refreshUsers, onError]);

  const changeToOrg = useCallback(async () => {
    if (!selectedUser) {
      return;
    }

    onPending();
    try {
      await api.put(`/api/users/${window.user?.id}`, { organizationId: selectedUser?.organization.id });
      onSuccess();
      window.location.reload();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [selectedUser, onError, onPending, onSuccess]);

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="user-info-actions" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>User Info and Actions</Accordion.Target>
          <Accordion.Body>
            <div className="internal-tool-component">
              <span className="internal-tool-text-input">
                <TextInput
                  label="Filter Users by Email"
                  placeholder="Filter Users by Email"
                  onChange={(value) => {
                    setEmailInput(value);
                    setUserSearch(value);
                  }}
                  value={emailInput}
            />
              </span>
              {userSearch.trim() === '' ? (
                <SearchPrompt />
              ) : (
                /* @ts-expect-error There are so many props in the table type that I don't think it's reasonable to fulfill them all. */
                <SelectableReactTable<UserCardUser | null>
                  data={users}
                  loading={status.loading}
                  loadingText="fetching users"
                  showPagination
                  showPaginationTop
                  showPaginationBottom={false}
                  defaultPageSize={10}
                  className="-striped -highlight"
                  getTheadThProps={() => ({ style: { textAlign: 'left' } })}
                  columns={[
                    {
                      Header: 'Email',
                      accessor: 'email',
                    },
                    {
                      Header: 'Username',
                      accessor: 'username',
                    },
                    {
                      Header: 'UserID',
                      accessor: 'id',
                    },
                    {
                      Header: 'org',
                      accessor: 'organization.title',
                    },
                    {
                      Header: 'orgID',
                      accessor: 'organization.id',
                    },
                    {
                      Header: 'Enabled',
                      accessor: 'enabled',
                      Cell: ({ value }: { value: boolean }) => (value === false ? 'No' : 'Yes'),
                    },
                    {
                      Header: 'API Token Count',
                      accessor: 'tokenCount',
                    },
                  ]}
                  selectedData={selectedUser}
                  onSelect={(user) => {
                    setSelectedUser(user);
                  }}
                  areEqual={(userA: UserCardUser | null, userB: UserCardUser | null) =>
                    userA?.id === userB?.id}
                  selectedBackgroundColor="#00afec"
                  selectedTextColor="black"
            />
              )}
              {selectedUser && (
              <>
                <div>
                  <Text.H2>Actions</Text.H2>
                  <ButtonGroup>
                    {selectedUser?.organization && (
                    <Button type={BUTTON_TYPE.SLATE} onClick={changeToOrg}>
                      Change to User&apos;s Org ({selectedUser.organization.title})
                    </Button>
                    )}
                    <Button type={BUTTON_TYPE.ERROR} onClick={onToggleDeleteModal}>
                      Delete {selectedUser.email}
                    </Button>
                    <Button type={BUTTON_TYPE.ERROR} onClick={onToggleChangeUserOrg}>
                      Change orgs for {selectedUser.email}
                    </Button>
                    {selectedUser.super && !isOnPrem && Number(selectedUser.organization.id) !== FOSSA_ORG_ID && (
                      <Button type={BUTTON_TYPE.SLATE} onClick={confirmMoveSelectedUserToFossa}>
                        Move {selectedUser.email} to FOSSA
                      </Button>
                    )}
                    {selectedUser.enabled ? (
                      <Button type={BUTTON_TYPE.ERROR} onClick={onToggleDeactivateModal}>
                        Deactivate {selectedUser.email}
                      </Button>
                    ) : (
                      <Button type={BUTTON_TYPE.SLATE} onClick={onToggleReactivateModal}>
                        Reactivate {selectedUser.email}
                      </Button>
                    )}
                  </ButtonGroup>
                </div>
                <ModalAreYouSure
                  executeAction={deleteSelectedUser}
                  actionName={`Delete ${selectedUser.email}`}
                  title={`Deleting User ${selectedUser.email}`}
                  show={isDeleteModalShowing}
                  onHide={onToggleDeleteModal}
                  verificationString={selectedUser.email}
              />
                <ModalAreYouSure
                  executeAction={updatedSelectedUser}
                  actionName={`Change Orgs for ${selectedUser.email}`}
                  title={`Change orgs for user ${selectedUser.email}`}
                  verificationString={selectedUser.email}
                  onHide={onToggleChangeUserOrg}
                  show={isChangeUserOrgModalShowing}
                  reversible
              >
                  <h3>Select {selectedUser.email}&apos;s new org</h3>
                  <OrganizationSearch searchOnly />
                </ModalAreYouSure>
                <ConfirmModal
                  isOpen={isDeactivateModalShowing}
                  title={`Deactivate ${selectedUser.email}`}
                  body={`Are you sure you want to deactivate ${selectedUser.email}? The user and their API tokens will be rendered inactive.`}
                  confirmText="Deactivate"
                  confirmBtnType="error"
                  onConfirm={deactivateSelectedUser}
                  onHide={onToggleDeactivateModal}
                />
                <ConfirmModal
                  isOpen={isReactivateModalShowing}
                  title={`Reactivate ${selectedUser.email}`}
                  body={`Are you sure you want to reactivate ${selectedUser.email}? The user will be re-enabled.`}
                  confirmText="Reactivate"
                  confirmBtnType="primary"
                  onConfirm={reactivateSelectedUser}
                  onHide={onToggleReactivateModal}
                />
              </>
              )}
            </div>
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default UserCard;
